/**
 * CUSTOM SIZE FORM
 * --------------------------------------------------------------------------
 * Driver for the "custom_size" product block (snippets/product-info.liquid).
 *
 * The customer types a width and a length. The element validates against the
 * product's min/max/step, shows the computed price (same formula as the n8n
 * workflow, which is the source of truth) and updates the <sale-price>
 * elements bound to the same form. On submit it resolves the size to a real
 * variant BEFORE Prestige's <form is="product-form"> adds it to cart:
 *
 *   submit (capture) → POST endpoint { product_id, width, length }
 *                    → { variant_id } → form.id = variant_id → requestSubmit()
 *
 * The submit listener sits on document in the capture phase so it always
 * runs before ProductForm's own listener, without touching theme.js.
 */
import { formatMoney } from 'theme';

const currencyFormat = window.themeVariables.settings.currencyCodeEnabled
  ? window.themeVariables.settings.moneyWithCurrencyFormat
  : window.themeVariables.settings.moneyFormat;

/**
 * Price formula. Must stay identical to the n8n workflow.
 * Returns whole kroner: base + m² × rate, rounded up to the nearest x90,
 * never below the minimum price.
 */
function calculatePrice(width, length, pricing) {
  const raw = pricing.base + (width * length / 10000) * pricing.perM2;
  const rounded = Math.ceil((raw + 10) / 100) * 100 - 10;
  return Math.max(rounded, pricing.min);
}

class CustomSizeForm extends HTMLElement {
  connectedCallback() {
    this.form = document.forms[this.getAttribute('form')];

    if (!this.form) {
      return;
    }

    this.config = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
    this.widthInput = this.querySelector('[data-size-width]');
    this.lengthInput = this.querySelector('[data-size-length]');
    this.summaryElement = this.querySelector('[data-size-summary]');
    this.priceElement = this.querySelector('[data-size-price]');
    this.salePriceElements = Array.from(document.querySelectorAll(`sale-price[form="${this.getAttribute('form')}"]`));
    this.originalSalePrices = this.salePriceElements.map((element) => element.lastChild.cloneNode(true));
    this.resolved = false;

    this.onInputListener = this.update.bind(this);
    this.onSubmitListener = this.onSubmit.bind(this);

    this.addEventListener('input', this.onInputListener);
    document.addEventListener('submit', this.onSubmitListener, true);

    this.update();
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.onInputListener);
    document.removeEventListener('submit', this.onSubmitListener, true);
  }

  get size() {
    return {
      width: parseInt(this.widthInput.value, 10),
      length: parseInt(this.lengthInput.value, 10)
    };
  }

  /**
   * Sets a custom validity message on a single input and returns whether it is valid.
   */
  validate(input) {
    const { min, max, step, strings } = this.config;
    const value = parseInt(input.value, 10);
    let message = '';

    if (input.value === '' || Number.isNaN(value)) {
      message = strings.required;
    } else if (value < min || value > max) {
      message = strings.range.replace('@min@', min).replace('@max@', max);
    } else if (value % step !== 0) {
      message = strings.step.replace('@step@', step);
    }

    input.setCustomValidity(message);

    return message === '';
  }

  /**
   * Recomputes the price and updates the summary + bound <sale-price> elements.
   * Returns whether both inputs are valid.
   */
  update() {
    const widthValid = this.validate(this.widthInput);
    const lengthValid = this.validate(this.lengthInput);

    if (!widthValid || !lengthValid) {
      this.summaryElement.textContent = this.config.strings.empty;
      this.priceElement.textContent = '';
      this.salePriceElements.forEach((element, index) => {
        element.lastChild.replaceWith(this.originalSalePrices[index].cloneNode(true));
      });

      return false;
    }

    const { width, length } = this.size;
    const formattedPrice = formatMoney(calculatePrice(width, length, this.config.pricing) * 100, currencyFormat);

    this.summaryElement.textContent = `${width} x ${length} cm`;
    this.priceElement.replaceChildren(document.createRange().createContextualFragment(formattedPrice));
    this.salePriceElements.forEach((element) => {
      element.lastChild.replaceWith(document.createRange().createContextualFragment(formattedPrice));
    });

    return true;
  }

  async onSubmit(event) {
    if (event.target !== this.form) {
      return;
    }

    // Second pass: the variant has been resolved, let ProductForm do its normal add-to-cart
    if (this.resolved) {
      this.resolved = false;
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (!this.update()) {
      this.form.reportValidity();
      return;
    }

    const submitButtons = Array.from(this.form.elements).filter((element) => element.type === 'submit');

    submitButtons.forEach((submitButton) => {
      submitButton.setAttribute('disabled', 'disabled');
      submitButton.setAttribute('aria-busy', 'true');
    });

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const { width, length } = this.size;
      const response = await fetch(this.config.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: this.config.productId, width, length })
      });
      const responseJson = await response.json().catch(() => ({}));

      if (!response.ok || !responseJson['variant_id']) {
        throw new Error(responseJson['error'] || this.config.strings.error);
      }

      this.form.elements.namedItem('id').value = responseJson['variant_id'];
      this.resolved = true;
      this.form.requestSubmit(); // ProductForm takes over from here (loading end + button state)
    } catch (error) {
      submitButtons.forEach((submitButton) => {
        submitButton.removeAttribute('disabled');
        submitButton.removeAttribute('aria-busy');
      });

      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      this.form.dispatchEvent(new CustomEvent('cart:error', {
        bubbles: true,
        detail: { error: error.message }
      }));
    }
  }
}

if (!window.customElements.get('custom-size-form')) {
  window.customElements.define('custom-size-form', CustomSizeForm);
}